// Messaging service for WhatsApp, Slack, Google Chat, and Teams
import { API_CONFIG, MESSAGING_API_ENDPOINTS } from '../helpers/constants';

const MESSAGING_API_BASE_URL = API_CONFIG.EMAIL_API_BASE_URL;

export interface ConnectMessagingRequest {
  provider: 'regular' | 'whatsapp' | 'slack' | 'google_chat' | 'teams';
  // Regular chat
  channel_name?: string;
  channel_description?: string;
  channel_business_rules?: string;
  workspace_tag?: string;
  workspace_id?: string;
  // WhatsApp
  access_token?: string;
  phone_number_id?: string;
  sync_interval?: number;
  // Slack
  bot_token?: string;
  // Google Chat
  service_account_key?: string;
  space_id?: string;
  // Teams
  client_id?: string;
  client_secret?: string;
  tenant_id?: string;
}

export interface ConnectMessagingResponse {
  success: boolean;
  message: string;
  provider: string;
  app_account_id: string | null;
  vault_id: string | null;
  sync_status: string | null;
  account_identifier: string | null;
  channel_id: string;
  workspace_id: string;
  channel_type: 'message';
}

// Google Chat Inbox — DM-only, OAuth2 real-mailbox provider. Deliberately a separate interface
// and function from ConnectMessagingRequest/connectMessaging: this is a structurally different
// payload (OAuth tokens, not a service-account key + space ID) hitting a different backend
// endpoint/schema (google_chat_inbox_connection.py), not a variant of the existing google_chat
// (named-space) flow. See docs/GoogleChatInbox_DM_FE_Implementation.md.
export interface ConnectGoogleChatInboxRequest {
  mailbox_email: string;
  access_token: string;
  refresh_token: string;
  google_client_id: string;
  google_client_secret: string;
  workspace_id?: string;
  workspace_tag?: string;
  channel_id?: string;
  channel_name?: string;
}

export type ConnectGoogleChatInboxResponse = ConnectMessagingResponse;

export const connectGoogleChatInbox = async (
  data: ConnectGoogleChatInboxRequest,
  authToken: string
): Promise<ConnectGoogleChatInboxResponse> => {
  try {
    const response = await fetch(`${MESSAGING_API_BASE_URL}${MESSAGING_API_ENDPOINTS.CONNECT_GOOGLE_CHAT_INBOX}`, {
      method: 'POST',
      headers: {
        'accept': 'application/json',
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      let errorMessage = `Google Chat Inbox connection failed: ${response.status}`;
      if (errorData.detail) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail) && errorData.detail.length > 0) {
          const firstError = errorData.detail[0];
          errorMessage = firstError?.msg || (typeof firstError === 'string' ? firstError : errorMessage);
        }
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.error) {
        errorMessage = errorData.error;
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error('Google Chat Inbox connection error:', error);
    throw error;
  }
};

export const connectMessaging = async (
  data: ConnectMessagingRequest,
  authToken: string
): Promise<ConnectMessagingResponse> => {
  try {
    const response = await fetch(`${MESSAGING_API_BASE_URL}${MESSAGING_API_ENDPOINTS.CONNECT}`, {
      method: 'POST',
      headers: {
        'accept': 'application/json',
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      let errorMessage = `Messaging connection failed: ${response.status}`;
      
      if (errorData.detail) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail) && errorData.detail.length > 0) {
          const firstError = errorData.detail[0];
          if (firstError.msg) {
            errorMessage = firstError.msg;
          } else if (typeof firstError === 'string') {
            errorMessage = firstError;
          }
        }
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.error) {
        errorMessage = errorData.error;
      }
      
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error('Messaging connection error:', error);
    throw error;
  }
};

export interface SendMessageRequest {
  provider: 'whatsapp' | 'slack' | 'google_chat' | 'teams';
  app_account_id?: string; // Optional, used for non-WhatsApp providers
  channel_id?: string; // Used for WhatsApp instead of app_account_id
  content: string;
  // WhatsApp specific
  recipient?: string; // phone number
  thread_id?: string; // wamid.xxxxxxxxxxxx
  // Slack specific
  slack_channel_id?: string;
  thread_ts?: string;
  // Google Chat specific
  space_id?: string;
  thread_name?: string;
  // Teams specific
  team_id?: string;
  reply_to_id?: string;
}

export interface SendMessageResponse {
  success: boolean;
  message: string;
  provider: string;
  message_id?: string;
  thread_id?: string;
  sent_at?: string;
  channel_id?: string;
  content_preview?: string;
  error?: string;
}

export const sendMessage = async (
  data: SendMessageRequest,
  authToken: string
): Promise<SendMessageResponse> => {
  try {
    const response = await fetch(`${MESSAGING_API_BASE_URL}${MESSAGING_API_ENDPOINTS.SEND_MESSAGE}`, {
      method: 'POST',
      headers: {
        'accept': 'application/json',
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      let errorMessage = `Failed to send message: ${response.status}`;
      
      if (errorData.detail) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail) && errorData.detail.length > 0) {
          const firstError = errorData.detail[0];
          if (firstError.msg) {
            errorMessage = firstError.msg;
          } else if (typeof firstError === 'string') {
            errorMessage = firstError;
          }
        }
      } else if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
      
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error('Send message error:', error);
    throw error;
  }
};

